'use client';

import React from 'react';
import { VStack, HStack, Text, Box, LucideIcon, type LucideIconName } from '@skin-seoul/design-system';
import { DeliveryTrackingStatus, ParxlTrackingStatus, YunExpressTrackingStatus, type TrackingEvent } from '@skin-seoul/service';

interface TrackingTimelineProps {
  events: TrackingEvent[];
}

const DEFAULT_ICON_NAME: LucideIconName = 'CircleCheck';

/**
 * Deleo tracking status icon mapping
 */
const DELEO_STATUS_ICONS: Record<DeliveryTrackingStatus, LucideIconName> = {
  [DeliveryTrackingStatus.Delivered]: 'CircleCheck',
  [DeliveryTrackingStatus.CustomsCleared]: 'CircleCheck',
  [DeliveryTrackingStatus.InCustoms]: 'Circle',
  [DeliveryTrackingStatus.ArrivedDestination]: 'Globe',
  [DeliveryTrackingStatus.Departed]: 'Plane',
  [DeliveryTrackingStatus.InvoiceIssued]: 'Box',
};

/**
 * PARXL tracking status icon mapping
 * Uses only icons available in @skin-seoul/design-system LUCIDE_ICONS
 */
const PARXL_STATUS_ICONS: Record<ParxlTrackingStatus, LucideIconName> = {
  // Success
  [ParxlTrackingStatus.Delivered]: 'CircleCheck',
  [ParxlTrackingStatus.CustomCleared]: 'CircleCheck',

  // Pending/Waiting
  [ParxlTrackingStatus.Booked]: 'Circle',
  [ParxlTrackingStatus.BookingReceived]: 'Circle',
  [ParxlTrackingStatus.AwaitingCollection]: 'Circle',

  // Pickup/Processing
  [ParxlTrackingStatus.PickedUp]: 'Box',
  [ParxlTrackingStatus.ReceivedAtExportHub]: 'Box',
  [ParxlTrackingStatus.ProcessingAtExportHub]: 'Box',

  // Transit
  [ParxlTrackingStatus.ReadyToTransfer]: 'Plane',
  [ParxlTrackingStatus.ReceivedAtAirlineTerminal]: 'Plane',
  [ParxlTrackingStatus.DepartedFromOrigin]: 'Plane',
  [ParxlTrackingStatus.InTransit]: 'Plane',

  // Arrival
  [ParxlTrackingStatus.ArrivedAtDestinationAirport]: 'Globe',
  [ParxlTrackingStatus.ArrivedAtCustomsFacility]: 'Globe',
  [ParxlTrackingStatus.EnrouteToSortingHub]: 'Globe',
  [ParxlTrackingStatus.ArrivedAtSortingHub]: 'Globe',
  [ParxlTrackingStatus.ArrivedAndProcessingAtSortingHub]: 'Globe',

  // Customs
  [ParxlTrackingStatus.HandedOverForCustoms]: 'PackageSearch',

  // Delivery
  [ParxlTrackingStatus.OutForDelivery]: 'Truck',

  // Failed/Cancelled
  [ParxlTrackingStatus.Cancelled]: 'CircleAlert',
  [ParxlTrackingStatus.Expired]: 'CircleAlert',
  [ParxlTrackingStatus.BookingRejected]: 'CircleAlert',
  [ParxlTrackingStatus.UnsuccessfulDelivery]: 'CircleAlert',
  [ParxlTrackingStatus.FinalUnsuccessfulDelivery]: 'CircleAlert',
  [ParxlTrackingStatus.Lost]: 'CircleAlert',

  // Issues
  [ParxlTrackingStatus.Disposed]: 'CircleAlert',
  [ParxlTrackingStatus.DamagedAtDestination]: 'CircleAlert',
  [ParxlTrackingStatus.Damaged]: 'CircleAlert',
  [ParxlTrackingStatus.PossibleDelay]: 'CircleAlert',

  // Modifications
  [ParxlTrackingStatus.Relabelled]: 'RefreshCwIcon',
  [ParxlTrackingStatus.Repackaged]: 'RefreshCwIcon',
  [ParxlTrackingStatus.Translated]: 'RefreshCwIcon',
  [ParxlTrackingStatus.AddressChange]: 'RefreshCwIcon',
};

/**
 * YunExpress tracking status icon mapping
 * Maps track_node_code values to icons
 */
const YUN_EXPRESS_STATUS_ICONS: Record<string, LucideIconName> = {
  // Delivered
  [YunExpressTrackingStatus.Delivered]: 'CircleCheck',

  // Return
  [YunExpressTrackingStatus.Returned]: 'CircleAlert',
  [YunExpressTrackingStatus.ReturnedBack]: 'CircleAlert',
  [YunExpressTrackingStatus.ReturnedToSender]: 'CircleAlert',

  // Processing / Order creation
  [YunExpressTrackingStatus.OrderCreation]: 'Circle',
  [YunExpressTrackingStatus.PreInfo]: 'Circle',
  [YunExpressTrackingStatus.PreAdvicing]: 'Circle',
  [YunExpressTrackingStatus.Edd]: 'Circle',

  // Pickup & First mile
  [YunExpressTrackingStatus.PickedUp]: 'Box',
  [YunExpressTrackingStatus.FirstMileArrive]: 'Box',
  [YunExpressTrackingStatus.FirstMileDepart]: 'Box',

  // Main line transit
  [YunExpressTrackingStatus.DepartConfirmOC]: 'Plane',
  [YunExpressTrackingStatus.ArriveConfirmOC]: 'Plane',
  [YunExpressTrackingStatus.MainLineDepart]: 'Plane',
  [YunExpressTrackingStatus.MainLineArrive]: 'Globe',
  [YunExpressTrackingStatus.InTransit]: 'Plane',
  [YunExpressTrackingStatus.TransitIn]: 'Plane',
  [YunExpressTrackingStatus.TransitOut]: 'Plane',
  [YunExpressTrackingStatus.TransithubArrive]: 'Globe',
  [YunExpressTrackingStatus.ChangeWaybillInfo]: 'RefreshCwIcon',
  [YunExpressTrackingStatus.PickupCargoTerminal]: 'Box',

  // Customs
  [YunExpressTrackingStatus.ArriveOrinCustoms]: 'PackageSearch',
  [YunExpressTrackingStatus.CustomsProcessing]: 'PackageSearch',
  [YunExpressTrackingStatus.CustomsComplete]: 'CircleCheck',
  [YunExpressTrackingStatus.CustomsRelease]: 'CircleCheck',
  [YunExpressTrackingStatus.ExportCustomsComplete]: 'CircleCheck',
  [YunExpressTrackingStatus.AirportRelease]: 'CircleCheck',

  // Last mile delivery
  [YunExpressTrackingStatus.CarrierPickup]: 'Truck',
  [YunExpressTrackingStatus.InTransitCarrier]: 'Truck',
  [YunExpressTrackingStatus.DeliveryAttempt]: 'Truck',

  // Abnormal / Exception
  [YunExpressTrackingStatus.AirportHold]: 'CircleAlert',
  [YunExpressTrackingStatus.AirportInspection]: 'CircleAlert',
  [YunExpressTrackingStatus.CustomsDelay]: 'CircleAlert',
  [YunExpressTrackingStatus.CustomsHold]: 'CircleAlert',
  [YunExpressTrackingStatus.CustomsInspection]: 'CircleAlert',
  [YunExpressTrackingStatus.DeliveryFailure]: 'CircleAlert',
  [YunExpressTrackingStatus.PackageException]: 'CircleAlert',
  [YunExpressTrackingStatus.PackageLost]: 'CircleAlert',
  [YunExpressTrackingStatus.Other]: 'CircleAlert',
};

/**
 * Gets the appropriate icon for a tracking event
 * Supports Deleo (enum-based), PARXL (string-based), and YunExpress (trackNodeCode) statuses
 */
const getIconForEvent = (eventType: string): LucideIconName => {
  // Check YunExpress statuses
  const yunExpressIcon = YUN_EXPRESS_STATUS_ICONS[eventType as YunExpressTrackingStatus];
  if (yunExpressIcon) {
    return yunExpressIcon;
  }

  // Check PARXL statuses (string-based)
  if (eventType in ParxlTrackingStatus) {
    return PARXL_STATUS_ICONS[eventType as ParxlTrackingStatus] ?? DEFAULT_ICON_NAME;
  }

  // Check if it's a PARXL status value (for direct string matching)
  const parxlIcon = PARXL_STATUS_ICONS[eventType as ParxlTrackingStatus];
  if (parxlIcon) {
    return parxlIcon;
  }

  // Fallback to Deleo enum-based mapping
  return DELEO_STATUS_ICONS[eventType as DeliveryTrackingStatus] ?? DEFAULT_ICON_NAME;
};

export const TrackingTimeline = ({ events }: TrackingTimelineProps) => {
  return (
    <VStack gap={4} width="100%" alignItems="flex-start">
      {events.map((event) => {
        const iconName = getIconForEvent(event.type as string);
        const iconColor = event.status === 'completed' ? 'success.600' : 'neutral.700';

        return (
          <React.Fragment key={event.id}>
            <HStack gap={4} width="100%" alignItems="flex-start">
              <Box width={5} height={5} borderRadius="50%" flexShrink={0} color={iconColor}>
                <LucideIcon name={iconName} width={5} height={5} />
              </Box>

              <VStack gap={1} flex="1" alignItems="flex-start">
                <Text textStyle="label.small" fontWeight="medium" color="neutral.700">
                  {event.title}
                </Text>
                <Text textStyle="label.small" fontWeight="regular" color="neutral.400">
                  {event.timestamp}
                </Text>
              </VStack>
            </HStack>
          </React.Fragment>
        );
      })}
    </VStack>
  );
};
